import { Play } from "lucide-react";
import {
  useCallback,
  useLayoutEffect,
  useState,
  type ReactNode
} from "react";

import type { PlayRendererHostProps } from "@/features/play";
import { useAccessibleActivation } from "@/platform/accessibility";
import { PrimaryCommandButton } from "@/ui";

import "./StandaloneActivityGate.css";

interface StandaloneActivityGateProps
  extends Pick<
    PlayRendererHostProps,
    "onCommand" | "onReady" | "sessionKey"
  > {
  readonly children: ReactNode;
  readonly instruction: string;
  readonly levelTitle: string;
  readonly onActivityStart?: () => void;
}

export function StandaloneActivityGate({
  sessionKey,
  ...props
}: StandaloneActivityGateProps) {
  return <StandaloneActivityGateSession {...props} key={sessionKey} />;
}

function StandaloneActivityGateSession({
  children,
  instruction,
  levelTitle,
  onActivityStart,
  onCommand,
  onReady
}: Omit<StandaloneActivityGateProps, "sessionKey">) {
  const [started, setStarted] = useState(false);

  useLayoutEffect(() => {
    onReady({});
  }, [onReady]);

  const start = useCallback(
    (inputMethod: "keyboard" | "pointer") => {
      onActivityStart?.();
      setStarted(true);
      onCommand({ type: "start", inputMethod });
    },
    [onActivityStart, onCommand]
  );
  const onStart = useAccessibleActivation<HTMLButtonElement>(start);

  return (
    <section
      className="standalone-activity-gate"
      data-started={started}
    >
      {!started ? (
        <div className="standalone-activity-gate__intro">
          <p className="standalone-activity-gate__eyebrow">先听点点说</p>
          <h2>{levelTitle}</h2>
          <p>{instruction}</p>
          <PrimaryCommandButton icon={Play} onClick={onStart}>
            我准备好了
          </PrimaryCommandButton>
        </div>
      ) : null}
      <div
        aria-label="游戏主区域"
        aria-hidden={!started || undefined}
        className="standalone-activity-gate__content"
        inert={!started ? true : undefined}
        role="group"
      >
        {children}
      </div>
    </section>
  );
}
